class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();

    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    // Price fields (range sliders + number inputs) must NOT submit on every "input"
    // event: a slider fires dozens of those per drag, and each one triggered a fetch
    // whose response re-rendered the form (destroying the slider mid-drag).
    // They submit on "change" instead (slider released / number field committed).
    const isPriceField = (el) =>
      el instanceof HTMLInputElement &&
      (el.type === "range" || el.type === "number");

    // Use event delegation on `this` (the custom element) instead of the inner form,
    // because AJAX re-renders replace the inner form element, losing listeners attached to it.
    this.addEventListener("input", (evt) => {
      if (isPriceField(evt.target)) return;
      this.handleFilterChange(evt);
    });

    this.addEventListener("change", (evt) => {
      if (!isPriceField(evt.target)) return;
      this.handleFilterChange(evt);
    });
  }

  handleFilterChange(evt) {
    const form = evt.target.closest("form");
    if (!form) return;
    FacetFiltersForm.loading.start();
    this.onSubmitHandler(evt);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.latestUrl = url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    window.scroll({
      top: FacetFiltersForm.section.offsetTop - 100,
      left: 0,
      behavior: "smooth",
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          { html, url },
        ];
        // Ignore stale responses that arrive after a newer request was made,
        // so an older result can't overwrite a newer one.
        if (url !== FacetFiltersForm.latestUrl) return;
        FacetFiltersForm.renderSection(html, event);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderSection(html, event);
  }

  static renderSection(html, event) {
    // renderFilters updates each filter block individually and skips the block
    // the user is interacting with. The whole form is no longer replaced, which
    // previously wiped out the price slider (and any focused input) on every render.

    // Hold on to the loader that dimmed the filters form when the request started.
    // renderElements() below swaps FacetFiltersForm.loading for a new instance, and
    // finishing that new one would leave the form stuck at low opacity.
    const loading = FacetFiltersForm.loading;

    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderElements(
      html,
      "[data-product-container]",
      FacetFiltersForm.selectors.productGrid
    );
    FacetFiltersForm.renderProductCount(html);

    const filtersForm = document.querySelector(
      FacetFiltersForm.selectors.filtersForm
    );
    if (filtersForm && typeof __reInitTooltip === "function") {
      __reInitTooltip(filtersForm);
    }
    // The form element persists now, so the same loader can be reused next time.
    loading.finish();
    FacetFiltersForm.loading = loading;

    document.dispatchEvent(new CustomEvent("collection:rerendered"));
  }

  static renderElements(html, el, selector) {
    const elementContainer = document.querySelector(el);
    elementContainer.innerHTML = new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(el).innerHTML;
    FacetFiltersForm.loading = new window.FoxTheme.AnimateLoading(
      document.body,
      { overlay: document.querySelector(selector) }
    );
    __reInitTooltip(elementContainer);
  }

  static renderProductCount(html) {
    const count = new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(FacetFiltersForm.selectors.productCount).innerHTML;
    const container = document.querySelector(
      FacetFiltersForm.selectors.productCount
    );
    container.innerHTML = count;
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");

    const facetDetailsElements = parsedHTML.querySelectorAll(
      "#FacetFiltersForm .js-filter"
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest(".js-filter") : undefined;
      return jsFilter
        ? element.dataset.index === jsFilter.dataset.index
        : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter(
      (element) => !matchesIndex(element)
    );

    facetsToRender.forEach((element) => {
      const target = document.querySelector(
        `.f-facets__block[data-index="${element.dataset.index}"]`
      );
      if (!target) return;
      target.textContent = "";
      const child =
        element.querySelector("collapsible-tab") ||
        element.querySelector("#Mobile-SortBy-Block");
      if (child) target.appendChild(child);
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [".active-facets"];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML =
        activeFacetsElement.innerHTML;
    });
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`
    );
  }

  static getSections() {
    return [
      {
        section: FacetFiltersForm.sectionId,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    if (form.id === "FacetFiltersForm" && !FoxThemeSettings.isMobile) {
      formData.delete("sort_by");
    }
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    if (FoxThemeSettings.isMobile) {
      const form = event.target.closest("form");
      if (!(form instanceof HTMLFormElement)) return;
      this.onSubmitForm(this.createSearchParams(form), event);
    } else {
      const forms = document.querySelectorAll("facet-filters-form form");
      const searchParams = [];
      forms.forEach((form) => searchParams.push(this.createSearchParams(form)));
      this.onSubmitForm(searchParams.join("&"), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    const url =
      event.currentTarget.href.indexOf("?") === -1
        ? ""
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf("?") + 1
          );
    FacetFiltersForm.renderPage(url);
  }
}
FacetFiltersForm.selectors = {
  container: ".f-facets__container",
  productGrid: "[data-products-grid]",
  productCount: "[data-product-count]",
  filtersForm: "[data-facet-form]",
  facetWrapper: "[data-facets-wrapper]",
  facetBlock: ".f-facets__block",
};

FacetFiltersForm.section = document.querySelector("#ProductGridContainer");
FacetFiltersForm.sectionId =
  FacetFiltersForm.section && FacetFiltersForm.section.dataset.sectionId;
FacetFiltersForm.domNodes = queryDomNodes(
  FacetFiltersForm.selectors,
  FacetFiltersForm.section
);
FacetFiltersForm.loading = new window.FoxTheme.AnimateLoading(document.body, {
  overlay: document.querySelector(FacetFiltersForm.selectors.filtersForm),
});

FacetFiltersForm.filterData = [];
FacetFiltersForm.latestUrl = null;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      inputs: ['input[type="number"]'],
      ranges: ['input[type="range"]'],
      minRange: '[data-type="min-range"]',
      maxRange: '[data-type="max-range"]',
      minInput: '[data-type="min-input"]',
      maxInput: '[data-type="max-input"]',
    };
    this.domNodes = queryDomNodes(this.selectors, this);
    this.priceGap = parseFloat(this.dataset.priceGap) || 0;
    this.priceMax = parseFloat(this.dataset.priceMax) || 0;

    this.domNodes.inputs.forEach((el) =>
      el.addEventListener("change", this.onInputChange.bind(this))
    );
    this.domNodes.ranges.forEach((el) =>
      el.addEventListener("input", this.onSliderChange.bind(this))
    );

    this.setMinAndMaxValues();
  }

  onSliderChange(event) {
    const input = event.currentTarget;
    const { minInput, maxInput, minRange, maxRange } = this.domNodes;
    const minVal = Number(minRange.value);
    const maxVal = Number(maxRange.value);

    if (input === minRange) {
      // Keep the min thumb at least `priceGap` below the max thumb
      if (maxVal - minVal < this.priceGap) {
        minRange.value = maxVal - this.priceGap;
      }
      minInput.value = this.formatPrice(minRange.value);
    } else if (input === maxRange) {
      // Keep the max thumb at least `priceGap` above the min thumb
      if (maxVal - minVal < this.priceGap) {
        maxRange.value = minVal + this.priceGap;
      }
      maxInput.value = this.formatPrice(maxRange.value);
    }

    this.updateTrack();
    this.setMinAndMaxValues();
  }

  onInputChange(event) {
    this.adjustToValidValues(event.currentTarget);

    // Move the slider thumbs to match what was typed
    const { minInput, maxInput, minRange, maxRange } = this.domNodes;
    minRange.value = minInput.value === "" ? 0 : minInput.value;
    maxRange.value = maxInput.value === "" ? this.priceMax : maxInput.value;

    this.updateTrack();
    this.setMinAndMaxValues();
  }

  updateTrack() {
    if (!this.priceMax) return;
    const { minRange, maxRange } = this.domNodes;
    this.style.setProperty(
      "--price-min",
      (100 * Number(minRange.value)) / this.priceMax + "%"
    );
    this.style.setProperty(
      "--price-max",
      (100 * Number(maxRange.value)) / this.priceMax + "%"
    );
  }

  setMinAndMaxValues() {
    const { minInput, maxInput } = this.domNodes;
    if (maxInput.value)
      minInput.setAttribute("max", Number(maxInput.value) - this.priceGap);
    if (minInput.value)
      maxInput.setAttribute("min", Number(minInput.value) + this.priceGap);
    if (minInput.value === "") maxInput.setAttribute("min", 0);
    if (maxInput.value === "")
      minInput.setAttribute("max", maxInput.getAttribute("max"));
  }

  adjustToValidValues(input) {
    if (input.value === "") return;
    const value = Number(input.value);
    const min = Number(input.getAttribute("min"));
    const max = Number(input.getAttribute("max"));

    let adjusted = value;
    if (value < min) adjusted = min;
    if (value > max) adjusted = max;

    // Always show two decimal places, e.g. 82 → 82.00
    input.value = this.formatPrice(adjusted);
  }

  // Sliders report whole numbers ("82"); price boxes should read "82.00".
  formatPrice(value) {
    const number = Number(value);
    return Number.isFinite(number) ? number.toFixed(2) : "";
  }
}

customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelector("a").addEventListener("click", (e) => {
      e.preventDefault();
      const form =
        this.closest("facet-filters-form") ||
        document.querySelector("facet-filters-form");
      form.onActiveFilterClick(e);
    });
  }
}

customElements.define("facet-remove", FacetRemove);

class ApplyButton extends HTMLElement {
  constructor() {
    super();
    const drawer = this.closest("drawer-component");
    this.querySelector("button").addEventListener("click", (e) => {
      e.preventDefault();
      drawer && drawer.closeDrawer(false);
    });
  }
}

customElements.define("facet-apply", ApplyButton);
